import { useMemo, useState } from 'react';

/**
 * Generic multi-step wizard. Pass an array of steps — each step is a React
 * element that receives `{state, setState}` props for shared form state, and
 * optionally sets `data-valid="true|false"` via an attribute on its wrapper
 * (but usually you'll just gate the Next button with the `canAdvance` prop).
 *
 * Caller contract:
 *
 *   <Wizard
 *     title="New assignment"
 *     steps={[
 *       { label: 'Type',   render: ({state, setState}) => <TypeStep ... />,
 *         canAdvance: (state) => !!state.type },
 *       { label: 'Length', render: ({state, setState}) => <LengthStep ... />,
 *         canAdvance: (state) => state.wordCount > 0 },
 *       ...
 *     ]}
 *     initialState={{ type: 'Essay', wordCount: 3000 }}
 *     submitLabel="Generate (1 credit)"
 *     onSubmit={async (state) => { ... }}
 *     onClose={() => setOpen(false)}
 *   />
 *
 * The wizard handles Next/Back buttons, a step indicator at the top, and
 * blocks forward navigation when `canAdvance` returns false.
 */
export default function Wizard({
  title,
  steps,
  initialState = {},
  submitLabel = 'Submit',
  onSubmit,
  onClose,
  busy = false,
  error = null,
}) {
  const [index, setIndex] = useState(0);
  const [state, setState] = useState(initialState);

  const current = steps[index];
  const isLast = index === steps.length - 1;
  const canAdvance = useMemo(
    () => (current?.canAdvance ? !!current.canAdvance(state) : true),
    [current, state],
  );

  function back() {
    if (index > 0) setIndex(index - 1);
  }
  function next() {
    if (!canAdvance) return;
    if (!isLast) setIndex(index + 1);
  }
  async function submit() {
    if (!canAdvance) return;
    try {
      await onSubmit(state);
    } catch {
      /* onSubmit owns its own error surface */
    }
  }

  return (
    <div
      className="wizard-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.55)',
        zIndex: 1050,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
      }}
      onClick={(e) => { if (e.target === e.currentTarget && onClose) onClose(); }}
    >
      <div
        className="wizard-shell"
        style={{
          background: '#fff',
          borderRadius: 16,
          width: '100%',
          maxWidth: 720,
          maxHeight: '92vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 24px 60px rgba(0,0,0,0.25)',
        }}
      >
        {/* Header */}
        <div
          style={{
            background: 'linear-gradient(135deg, var(--primary), var(--secondary))',
            color: '#fff',
            padding: '1.1rem 1.4rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '0.6rem',
          }}
        >
          <div>
            <h4 className="mb-0" style={{ fontWeight: 700 }}>{title}</h4>
            <div className="small mt-1" style={{ opacity: 0.85 }}>
              Step {index + 1} of {steps.length} — {current?.label}
            </div>
          </div>
          {onClose && (
            <button
              type="button"
              className="btn btn-sm"
              onClick={onClose}
              style={{
                background: 'rgba(255,255,255,0.15)',
                color: '#fff',
                borderColor: 'rgba(255,255,255,0.2)',
              }}
              aria-label="Close"
            >
              <i className="fas fa-times me-1"></i>Close
            </button>
          )}
        </div>

        {/* Step pills */}
        <div
          style={{
            padding: '0.9rem 1.4rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            flexWrap: 'wrap',
            borderBottom: '1px solid var(--border)',
            background: '#fafbfd',
          }}
        >
          {steps.map((s, i) => (
            <div key={s.label} className="d-flex align-items-center" style={{ flex: '0 0 auto' }}>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 26, height: 26, borderRadius: 13,
                  background:
                    i < index ? 'var(--primary)'
                    : i === index ? 'var(--primary)'
                    : 'var(--surface-tint, #e8ecf3)',
                  color: i <= index ? '#fff' : 'var(--muted, #666)',
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                {i < index ? <i className="fas fa-check" style={{ fontSize: 11 }}></i> : i + 1}
              </span>
              <span
                className="ms-2 small"
                style={{
                  color: i === index ? 'var(--primary)' : 'var(--muted, #666)',
                  fontWeight: i === index ? 600 : 400,
                  whiteSpace: 'nowrap',
                }}
              >
                {s.label}
              </span>
              {i < steps.length - 1 && (
                <span
                  className="mx-2 d-none d-sm-inline"
                  style={{ width: 24, height: 1, background: 'var(--border)' }}
                />
              )}
            </div>
          ))}
        </div>

        {/* Step body */}
        <div
          style={{
            padding: '1.5rem 1.6rem',
            overflowY: 'auto',
            flex: 1,
          }}
        >
          {error && (
            <div className="alert alert-danger" role="alert">
              {typeof error === 'string' ? error : 'Something went wrong — try again.'}
            </div>
          )}
          {current?.render({ state, setState })}
        </div>

        {/* Footer nav */}
        <div
          style={{
            padding: '0.9rem 1.4rem',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            gap: '0.6rem',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            background: '#fafbfd',
          }}
        >
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={back}
            disabled={index === 0 || busy}
          >
            <i className="fas fa-arrow-left me-1"></i>Back
          </button>
          <div className="d-flex gap-2">
            {!isLast && (
              <button
                type="button"
                className="btn btn-cta"
                onClick={next}
                disabled={!canAdvance || busy}
              >
                Next <i className="fas fa-arrow-right ms-1"></i>
              </button>
            )}
            {isLast && (
              <button
                type="button"
                className="btn btn-cta"
                onClick={submit}
                disabled={!canAdvance || busy}
              >
                {busy ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1" role="status" />
                    Submitting…
                  </>
                ) : (
                  <>{submitLabel}<i className="fas fa-check ms-1"></i></>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


/**
 * Floating action button — for the "+ New …" entry point on list pages.
 */
export function FloatingAction({ label, icon = 'fa-plus', onClick }) {
  return (
    <button
      type="button"
      className="btn btn-cta"
      onClick={onClick}
      style={{
        position: 'fixed',
        bottom: '1.5rem',
        right: '1.5rem',
        borderRadius: 999,
        padding: '0.85rem 1.4rem',
        fontWeight: 600,
        boxShadow: '0 10px 30px rgba(79,70,229,0.35)',
        zIndex: 40,
      }}
    >
      <i className={`fas ${icon} me-2`}></i>
      {label}
    </button>
  );
}
